import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { dateKeyInTimeZone } from '@/lib/date-values';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { leadAnalyticsReportEndpoints } from '@/lib/api/endpoints';
import { leadKeys } from '@/lib/queryKeys';
import { exportRowsAsXLSX } from '@/lib/export';
import {
  AnalyticsReportShell,
  BreakdownTable,
  MetricCard,
  ReportBarChart,
  ReportDateRangeControl,
  ReportFunnelChart,
  ReportLineChart,
  ReportSection,
  ReportTeamFilter,
  ScorecardTable,
  computeDeltaPct,
  formatCurrency,
  formatDuration,
  formatInteger,
  formatPercent,
  useDrillToList,
  useReportPeriod,
  type ScorecardColumn,
  type TeamFilterOption,
} from '@/components/organisms/analytics-reports';

interface LeadMetrics {
  totalLeads: number;
  conversionRate: number;
  avgSpeedToLeadSec: number | null;
  pipelineValue: number;
  avgTimeToCloseSec: number | null;
  revenuePerLead: number;
  slaCompliance: number | null;
}
interface BySourceRow {
  source: string;
  count: number;
  won: number;
  conversionRate: number;
  value: number;
}
interface SourceEconomicsRow {
  source: string;
  leads: number;
  won: number;
  conversionRate: number;
  revenue: number;
  revenuePerLead: number;
  sourceSpend: number | null;
  costPerLead: number | null;
  returnOnAdSpend: number | null;
}
interface ByRepRow {
  assignedTo: string;
  repName: string;
  leads: number;
  avgResponseSec: number | null;
  conversionRate: number;
  revenue: number;
}
interface VolumeForecastRow {
  source: string;
  historicalMonths: number;
  monthlyAverage: number;
  projectedLeads: number;
}
interface VolumeForecast {
  status: 'READY' | 'INSUFFICIENT_HISTORY';
  reason: string | null;
  nextMonthStart: string | null;
  bySource: VolumeForecastRow[];
}
interface SelfBookedSegment {
  count: number;
  won: number;
  conversionRate: number;
  revenue: number;
}

interface LeadOverview {
  period: { from: string; to: string };
  metrics: LeadMetrics;
  metricsPrevious: LeadMetrics;
  selfBooked: SelfBookedSegment;
  funnel: { stage: string; count: number }[];
  bySource: BySourceRow[];
  sourceEconomics: SourceEconomicsRow[];
  volumeForecast: VolumeForecast;
  byRep: ByRepRow[];
  lossAnalysis: { lossReason: string; count: number }[];
  speedTrend: { period: string; value: number }[];
  aging: { stage: string; avgAgeDays: number }[];
  teamOptions: TeamFilterOption[];
}

export const Route = createFileRoute('/_dashboard/dashboard/leads/reports')({
  component: LeadReportsPage,
});

const repColumns: ScorecardColumn<ByRepRow>[] = [
  { key: 'rep', header: 'Rep', type: 'text', value: (r) => r.repName },
  {
    key: 'leads',
    header: 'Leads',
    type: 'number',
    value: (r) => r.leads,
    total: (rows) => rows.reduce((s, r) => s + r.leads, 0),
  },
  { key: 'response', header: 'Avg Response', type: 'duration', value: (r) => r.avgResponseSec },
  { key: 'conversion', header: 'Conversion', type: 'percent', value: (r) => r.conversionRate },
  {
    key: 'revenue',
    header: 'Revenue',
    type: 'currency',
    value: (r) => r.revenue,
    total: (rows) => rows.reduce((s, r) => s + r.revenue, 0),
  },
];

const sourceColumns: ScorecardColumn<BySourceRow>[] = [
  { key: 'source', header: 'Source', type: 'text', value: (r) => r.source },
  {
    key: 'count',
    header: 'Leads',
    type: 'number',
    value: (r) => r.count,
    total: (rows) => rows.reduce((s, r) => s + r.count, 0),
  },
  { key: 'conversion', header: 'Conversion', type: 'percent', value: (r) => r.conversionRate },
  {
    key: 'value',
    header: 'Value',
    type: 'currency',
    value: (r) => r.value,
    total: (rows) => rows.reduce((s, r) => s + r.value, 0),
  },
];

function formatNullableCurrency(value: number | null | undefined): string {
  return value == null ? 'Not configured' : formatCurrency(value);
}

function formatNullableRatio(value: number | null | undefined): string {
  return value == null ? '--' : `${value.toFixed(1)}x`;
}

const sourceEconomicsColumns: ScorecardColumn<SourceEconomicsRow>[] = [
  { key: 'source', header: 'Source', type: 'text', value: (r) => r.source },
  {
    key: 'leads',
    header: 'Leads',
    type: 'number',
    value: (r) => r.leads,
    total: (rows) => rows.reduce((s, r) => s + r.leads, 0),
  },
  { key: 'conversion', header: 'Conversion', type: 'percent', value: (r) => r.conversionRate },
  {
    key: 'revenue',
    header: 'Revenue',
    type: 'currency',
    value: (r) => r.revenue,
    total: (rows) => rows.reduce((s, r) => s + r.revenue, 0),
  },
  {
    key: 'revenuePerLead',
    header: 'Revenue / Lead',
    type: 'currency',
    value: (r) => r.revenuePerLead,
  },
  {
    key: 'sourceSpend',
    header: 'Spend',
    type: 'currency',
    value: (r) => r.sourceSpend,
    render: (r) => formatNullableCurrency(r.sourceSpend),
  },
  {
    key: 'costPerLead',
    header: 'Cost / Lead',
    type: 'currency',
    value: (r) => r.costPerLead,
    render: (r) => formatNullableCurrency(r.costPerLead),
  },
  {
    key: 'returnOnAdSpend',
    header: 'ROAS',
    type: 'number',
    value: (r) => r.returnOnAdSpend,
    render: (r) => formatNullableRatio(r.returnOnAdSpend),
  },
];

const forecastColumns = [
  {
    key: 'source',
    header: 'Source',
    align: 'left' as const,
    render: (r: VolumeForecastRow) => r.source,
  },
  {
    key: 'months',
    header: 'History',
    align: 'right' as const,
    render: (r: VolumeForecastRow) => `${formatInteger(r.historicalMonths)} mo`,
  },
  {
    key: 'average',
    header: 'Monthly Avg',
    align: 'right' as const,
    render: (r: VolumeForecastRow) => r.monthlyAverage.toFixed(1),
  },
  {
    key: 'projected',
    header: 'Projected Leads',
    align: 'right' as const,
    render: (r: VolumeForecastRow) => formatInteger(r.projectedLeads),
  },
];

function formatPercentOutOf100(value: number | null | undefined): string {
  if (value == null) return '--';
  return formatPercent(value / 100);
}

function LeadReportsPage() {
  const period = useReportPeriod('analytics.leads.reportPeriod');
  const [ownerId, setOwnerId] = useState<string | undefined>(undefined);
  const params = { from: period.from, to: period.to, ownerId };
  const drillTo = useDrillToList();

  const q = useApiQuery<LeadOverview>({
    queryKey: leadKeys.report('overview', params),
    queryFn: () =>
      apiGet<LeadOverview>(operationsClient, leadAnalyticsReportEndpoints.overview(params)),
    showErrorToast: false,
  });
  const data = q.data;
  const m = data?.metrics;
  const prev = data?.metricsPrevious;
  const sb = data?.selfBooked;

  async function handleExport() {
    if (!data) return;
    const rows = data.byRep.map((r) => ({
      Rep: r.repName,
      Leads: r.leads,
      'Avg Response': formatDuration(r.avgResponseSec),
      Conversion: formatPercent(r.conversionRate),
      Revenue: r.revenue,
    }));
    // Period bounds are UTC-snapped ISO instants; label the file with their UTC
    // calendar date (the same boundary the backend filters on).
    const fname = `leads-report-${dateKeyInTimeZone(period.from, 'UTC')}_${dateKeyInTimeZone(period.to, 'UTC')}.xlsx`;
    await exportRowsAsXLSX(rows, fname, { currencyColumns: ['Revenue'] });
  }

  return (
    <AnalyticsReportShell
      title="Lead Reports"
      subtitle="Pipeline performance and analytics"
      backTo="/dashboard/leads"
      onExport={handleExport}
      exportDisabled={!data}
      actions={
        <>
          <ReportTeamFilter
            value={ownerId}
            onChange={setOwnerId}
            options={data?.teamOptions ?? []}
            allLabel="All reps"
          />
          <ReportDateRangeControl period={period} />
        </>
      }
    >
      <ReportSection title="Key metrics">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7">
          <MetricCard
            title="Total Leads"
            value={m ? formatInteger(m.totalLeads) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(m?.totalLeads, prev?.totalLeads)}
          />
          <MetricCard
            title="Conversion Rate"
            value={m ? formatPercent(m.conversionRate) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(m?.conversionRate, prev?.conversionRate)}
          />
          <MetricCard
            title="Avg Speed-to-Lead"
            value={m ? formatDuration(m.avgSpeedToLeadSec) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(m?.avgSpeedToLeadSec, prev?.avgSpeedToLeadSec)}
            deltaPositiveIsGood={false}
          />
          <MetricCard
            title="SLA Compliance"
            value={m ? formatPercentOutOf100(m.slaCompliance) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(m?.slaCompliance, prev?.slaCompliance)}
          />
          <MetricCard
            title="Pipeline Value"
            value={m ? formatCurrency(m.pipelineValue) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(m?.pipelineValue, prev?.pipelineValue)}
          />
          <MetricCard
            title="Avg Time to Close"
            value={m ? formatDuration(m.avgTimeToCloseSec) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(m?.avgTimeToCloseSec, prev?.avgTimeToCloseSec)}
            deltaPositiveIsGood={false}
          />
          <MetricCard
            title="Revenue / Lead"
            value={m ? formatCurrency(m.revenuePerLead) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(m?.revenuePerLead, prev?.revenuePerLead)}
          />
        </div>
      </ReportSection>

      <ReportSection
        title="Self-booked"
        description="Leads booked at creation (online / phone / direct job) — excluded from speed-to-lead, shown here so their volume and revenue stay visible."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Self-booked leads"
            value={sb ? formatInteger(sb.count) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Booked (won)"
            value={sb ? formatInteger(sb.won) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Conversion"
            value={sb ? formatPercent(sb.conversionRate) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Self-booked revenue"
            value={sb ? formatCurrency(sb.revenue) : '--'}
            isLoading={q.isLoading}
          />
        </div>
      </ReportSection>

      <ReportSection title="Conversion funnel">
        <ReportFunnelChart
          title="Leads by stage"
          isLoading={q.isLoading}
          stages={data?.funnel ?? []}
        />
      </ReportSection>

      <ReportSection title="By source">
        <ReportBarChart
          title="Leads by source"
          isLoading={q.isLoading}
          data={(data?.bySource ?? []).map((s) => ({ label: s.source, value: s.count }))}
        />
        <ScorecardTable<BySourceRow>
          rows={data?.bySource ?? []}
          isLoading={q.isLoading}
          emptyMessage="No source data in this range"
          initialSortKey="count"
          columns={sourceColumns}
          onRowClick={(r) => drillTo('leads', { source: r.source })}
        />
      </ReportSection>

      <ReportSection
        title="Source economics"
        description="Revenue economics by source; spend-derived fields stay unavailable until source spend exists."
      >
        <ScorecardTable<SourceEconomicsRow>
          rows={data?.sourceEconomics ?? []}
          isLoading={q.isLoading}
          emptyMessage="No source economics in this range"
          initialSortKey="revenue"
          columns={sourceEconomicsColumns}
          onRowClick={(r) => drillTo('leads', { source: r.source })}
        />
      </ReportSection>

      <ReportSection title="Rep performance">
        <ScorecardTable<ByRepRow>
          rows={data?.byRep ?? []}
          isLoading={q.isLoading}
          emptyMessage="No leads assigned to a rep in this range"
          initialSortKey="leads"
          columns={repColumns}
        />
      </ReportSection>

      <ReportSection
        title="Lead volume forecast"
        description="Projected next-month lead volume by source when enough historical data exists."
      >
        {q.isLoading ? (
          <BreakdownTable<VolumeForecastRow> rows={[]} isLoading columns={forecastColumns} />
        ) : data?.volumeForecast?.status === 'READY' ? (
          <BreakdownTable<VolumeForecastRow>
            rows={data.volumeForecast.bySource}
            emptyMessage="No forecast data available"
            columns={forecastColumns}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            {data?.volumeForecast?.reason ?? 'Forecast unavailable for this range.'}
          </p>
        )}
      </ReportSection>

      <ReportSection title="Loss analysis">
        <BreakdownTable
          rows={data?.lossAnalysis ?? []}
          isLoading={q.isLoading}
          emptyMessage="No lost leads in this range"
          columns={[
            { key: 'reason', header: 'Loss Reason', align: 'left', render: (r) => r.lossReason },
            {
              key: 'count',
              header: 'Count',
              align: 'right',
              render: (r) => formatInteger(r.count),
            },
          ]}
        />
      </ReportSection>

      <ReportSection title="Speed-to-lead trend">
        <ReportLineChart
          title="Avg speed-to-lead by month"
          isLoading={q.isLoading}
          points={(data?.speedTrend ?? []).map((p) => ({ period: p.period, value: p.value }))}
        />
      </ReportSection>

      <ReportSection title="Pipeline aging">
        <BreakdownTable
          rows={data?.aging ?? []}
          isLoading={q.isLoading}
          emptyMessage="No open leads in this range"
          columns={[
            { key: 'stage', header: 'Stage', align: 'left', render: (r) => r.stage },
            {
              key: 'age',
              header: 'Avg Age (days)',
              align: 'right',
              render: (r) => r.avgAgeDays.toFixed(1),
            },
          ]}
        />
      </ReportSection>
    </AnalyticsReportShell>
  );
}
